import { useContext, useEffect, useState } from "react";

import AddIcon from '@mui/icons-material/Add';
import { Button, CircularProgress, Dialog, DialogActions } from "@mui/material";
import classNames from "classnames";
import "react-leaflet-fullscreen/styles.css";
import { getAllOrganisations } from "../api/super-admin";
import { deleteValidation as apiDeleteValidation, createOrUpdateValidationApi, loadValidation } from "../api/validations";
import { MapDataContext } from '../context/map-data';
import { Shape, ShapeOption, Validation, ValidationCombinedFile } from "../core/global";
import { createValidation, createValidationItem, createValidationSuperAdmin } from "../data/form-shapes";
import styles from "../styles/components/organisation-validations-modal.module.scss";
import { DeleteConfirmation } from "./confirmation-buttons";
import { ErrorMessage, WarningMessage } from "./error-message";
import { CreateForm } from "./form-components";
import { ModalTitle } from "./modal-title";
import { ValidationItemRow } from "./organisation-validations";

interface ValidationModalProps {
  id: number | string | null
  onClose: () => void
  editedOrganisationId: number | string | null | number[] | string[]
  isSuperAdmin?: boolean
}

export const ValidationModal = ({
  id,
  onClose,
  editedOrganisationId,
  isSuperAdmin = false,
}: ValidationModalProps) => {
  const [currentValidation, setCurrentValidation] = useState<Validation[]>([])
  const [loading, setLoading] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [validationImageErrors, setValidationImageErrors] = useState<string[]>([])
  const [showAddItem, setShowAddItem] = useState(false)
  const [formValues, setFormValues] = useState<any>({})
  const [formValidationBody, setFormValidationBody] = useState<any>({})
  const [formValidationFile, setFormValidationFile] = useState<any>([])
  const [useCreateValidation, setUseCreateValidation] = useState<Shape[]>([])

  const {
    dataSources,
  } = useContext(MapDataContext);

  useEffect(() => {
    setErrors([])
    setShowWarning(false)
    setLoading(true)
    const execute = async () => {
      const isEmpty = !id
      if (isEmpty) {
        setFormValidationBody({})
        setFormValidationFile([])
      } else {
        const res = await loadValidation({id})

        const {error, response, message} = res
    
        if (error === 200 && response) {
          const results = response || []
          setCurrentValidation(results as Validation[])
          setErrors([])
        } else {
          setErrors([message || "An error occurred while fetching validations."])
        }
      }
      setLoading(false)
    }
    execute()
  }, [id])

  useEffect(() => {
    if (currentValidation === null || currentValidation.length === 0) return

    const {name,organisation_id, geom, provider = null, model = null, geography = null, version = null} = currentValidation![0]
    const [ lon, lat] = (geom || '').replace(/(POINT)|[()]/gi, '').split(' ')
    setFormValidationBody({
      name,
      data_source: `${provider}-${model}-${geography}-${version}`,
      lon,
      lat,
      organisation_id,
    })
    const files = currentValidation.map( validation => {
      const {content, file, type, validations_content_id} = validation
      if (!file) return null
      return {
        content, file, type, validations_content_id
      }
    }).filter( item => item !== null)
    setFormValidationFile(files)
  }, [currentValidation])

  useEffect(() => {
    let model = null
    let provider = null
    let geography = null
    let version = null
    if (dataSources) {
      const {
        data_source
      } = formValidationBody
      if (data_source) {
        const foundDataSource = dataSources[data_source]
        if (foundDataSource) {
          model = foundDataSource.model || null
          provider = foundDataSource.provider || null
          geography = foundDataSource.geography || null
          version = foundDataSource.version || null
        }
      }
    }
    const existingFiles = formValidationFile.filter( (file: any) => typeof file.file === 'string' && file.file)
    const newFiles = formValidationFile.filter( (file: any) => typeof file.file !== 'string' && file.file)

    setFormValues({
        model, 
        provider, 
        geography, 
        version,
        organisation_id: editedOrganisationId,
        ...formValidationBody,
        new_files: newFiles,
        existing_files: existingFiles
      })

    },[dataSources, editedOrganisationId, formValidationBody, formValidationFile])

  useEffect(() => {
    const execute = async () => {
      let useCreateValidation = [...createValidation]
      if (isSuperAdmin) {
        useCreateValidation = [...createValidationSuperAdmin]
        // get add organisations and fill them into drop down
        const res = await getAllOrganisations()
        const {error, response, message} = res
        
        if (error === 200 && response) {
          const results = response?.results || []
          const options: ShapeOption[] = results.map((item: any) => {
            const { id, name } = item
            return {key: `${id}`, value: name}
          }).filter( (item: any) => item !== null) as ShapeOption[]
          
          // return with updated data_source
          useCreateValidation = useCreateValidation.map( item => {
            if (item.key === 'organisation_id') {
              return {...item, options}
            }
            return item
          })
        } else {
          useCreateValidation = [...createValidation]
        }
      }  
  
      if (dataSources) {
        const options: ShapeOption[] = Object.keys(dataSources).map((key: any) => {
          if (dataSources !== null) {
            const { name } = dataSources[key]
            return {key: `${key}`, value: name}
          }
          return null
        }).filter( item => item !== null) as ShapeOption[]
        
        // return with updated data_source
        useCreateValidation = useCreateValidation.map( item => {
          if (item.key === 'data_source') {
            return {...item, options}
          }
          return item
        })
      }
      setUseCreateValidation(useCreateValidation)
    }
    execute()
  }, [createValidation, dataSources])
  
  const validateInputs = () => {
    let isValid = true;
    const {
      data_source, 
      new_files,
      existing_files,
      lat, 
      lon, 
      name, 
    } = formValues
    let useError: string[] = []
    const files = [...(new_files || []), ...(existing_files || [])]
    if (!files || (files && Array.isArray(files) && files.length === 0)) {
      isValid = false;
      useError.push(`You must add at least one validation image.`)
    }
    if (!data_source) {
      isValid = false;
      useError.push(`You must select a data source.`)
    }
    if (!name) {
      isValid = false;
      useError.push(`You must give name to your location.`)
    }
    if (lat === undefined || lat === null || lon === undefined || lon === null) {
      isValid = false;
      useError.push(`You must set latitude and longitude for the validation point.`)
    }
    
    setErrors( e => {
      // remove duplicate messages
      useError = Array.from(new Set([...useError]))
      return useError
    })
    return isValid;
  };
  

  return (
    <>
      <Dialog
        id="create-or-update-validation-modal"
        open
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <ModalTitle onClose={onClose}>{!id ? `Create` : `Edit your`} validation point</ModalTitle>
        {loading && 
          <div className={classNames(styles.dialogContent, styles.loading)}>
            <CircularProgress aria-label="Loading…" />
          </div>
        }
        {!loading && 
          <div className={styles.dialogContent}>
            <ErrorMessage message={errors} />
            <CreateForm
              allowEdit={false}
              isModal={false}
              shape={useCreateValidation}
              onChange={(vals, key) => {
                setFormValidationBody((forValues: any) => {
                  return {...forValues, ...vals}
                })
              }}
              successMessage="New validation point has been added"
              defaultValues={formValidationBody}
            />
            <Button
              className={styles.addButton}
              variant="contained"
              startIcon={<AddIcon sx={{color: '#ffffff'}} />}
              onClick={()=> {
                setShowAddItem(true)
                setValidationImageErrors([])
              }}
            >
              {`Add validation image(s)`}
            </Button>
            {showWarning && <WarningMessage message={['Lorem ipsum...']} />}
            <ValidationItemRow 
              showIcon={false} 
              items={formValidationFile} 
              canEdit={!!(id)} 
              onEdit={(item: ValidationCombinedFile) => {
                // remove item from the list of files
                setFormValidationFile((files: any) => {
                  return files.filter( (file: any) => {
                    if (item.validations_content_id && file.validations_content_id) {
                      return file.validations_content_id !== item.validations_content_id
                    }
                    return true
                  })
                })
              }}
            />
          </div>
        }
        <DialogActions sx={{ pb: 3, px: 3, justifyContent: 'space-between' }}>
          <div>
            {id && <DeleteConfirmation 
              onDelete={() => {
                const execute = async () => {
                  const apiResponse: any = await apiDeleteValidation({ organisation_id: null, validation_id: id })
                  if (apiResponse !== null) {
                    const {response, error, message} = apiResponse
                    if (error !== 200) {
                      setErrors( e => [`${message}`])
                    } else {
                      setErrors([])
                      onClose()
                    }
                  }
                }
                execute()
              }}
            />}
          </div>
          <div>
            <Button 
              disabled={loading} 
              onClick={onClose}>
                Cancel
            </Button>
            <Button 
              variant="contained"
              type="submit"
              onClick={() => {
                const isValid = validateInputs()
                if (isValid) {
                  setLoading(true)
                  const execute = async () => {
                    await createOrUpdateValidationApi({
                      id,
                      ...formValues
                    })  
                    onClose()
                    setLoading(false)
                  }
                  execute()
                }
              }}
              disabled={loading}
            >
              Save
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      {showAddItem && 
        <CreateForm
          title={'Upload a validation image'}
          intro={<>
            <p>Upload your file(s), currently only jpg and png are supported.</p>
              <ErrorMessage message={validationImageErrors} />
            </>
          }
          open={showAddItem}
          isModal={true}
          shape={createValidationItem}
          successMessage="New validation point has been added"
          defaultValues={null}
          onCancel={() => {
            setShowAddItem(false)
          }}
          onSuccess={(res) => {
            setShowAddItem(false)
            setFormValidationFile([...formValidationFile, ...res])
          }}
          onSubmit={(vals ) => {
            const {file, type, content} = vals
            const fileItems = [...file].map((fileItem: any) => {
              return {
                ...vals,
                file: fileItem,
              }
            })
            const response = {
              response: [...fileItems], 
              error: 200, 
              message: ''
            }
            // create promise and return response const
            return new Promise((resolve, reject) => {
              resolve(response)
            })
          }}
        />
      }
    </>
  )
}
